import type { FeatureContent } from "@/lib/features/content";

export const ID_FEATURES: Partial<Record<string, FeatureContent>> = {
  "auto-clip": {
    related: [
      "/use-cases/clippers",
      "/use-cases/podcast-clipping",
      "/calculators/clips-from-one-video",
      "/compare/crayo-vs-opus-clip",
      "/compare/crayo-vs-vizard",
      "/compare/crayo-vs-klap",
    ],
    title: "Auto Clip",
    answer:
      "Auto Clip mengambil video panjang yang kamu upload, seperti podcast, stream, atau webinar, lalu memakai AI untuk menemukan dan memotong momen terkuat jadi klip pendek lengkap dengan caption. Dibuat untuk clipper, podcaster, streamer, dan agensi yang mengubah rekaman panjang jadi video pendek dalam jumlah besar.",
    description:
      "Upload video panjang dan biarkan AI menemukan momen terbaik, lalu memotongnya jadi klip yang siap diposting.",
    heroPrefix: "Ubah video panjang jadi",
    heroHighlight: "klip viral",
    heroSuffix: "secara otomatis",
    heroImage: "/features/auto-clip/auto-clip-hero.png",
    buttonText: "Potong Klip Sekarang",
    buttonLink: "/create/auto-clip",
    seoTitle: "Auto Clipper AI: Potong Klip Otomatis",
    seoDescription:
      "Upload podcast, stream, atau video panjang apa pun dan biarkan AI memotong momen terbaik jadi klip pendek viral dalam hitungan detik.",
    faqs: [
      {
        question: "Bagaimana Auto Clip memilih momennya?",
        answer:
          "AI memindai seluruh video untuk mencari hook, seperti cerita, punchline, dan bagian dengan energi tinggi, lalu memberi skor pada setiap klip supaya kamu bisa memposting yang paling kuat lebih dulu.",
      },
      {
        question: "Seberapa panjang video yang bisa saya upload?",
        answer:
          "Video panjang memang intinya: podcast, stream, dan webinar semuanya cocok. Video yang lebih panjang hanya butuh waktu proses sedikit lebih lama.",
      },
      {
        question: "Apakah klipnya sudah ada subtitle?",
        answer:
          "Ya. Setiap klip otomatis mendapat caption yang akurat dan sudah bergaya, dan kamu bisa menyesuaikan tampilannya sebelum export.",
      },
      {
        question: "Bisa edit klip sebelum diposting?",
        answer:
          "Bisa. Buka klip mana pun di editor untuk memotong, reframe, atau mengubah gaya caption sebelum export.",
      },
    ],
  },
  "ai-captions": {
    related: [
      "/features/auto-clip",
      "/features/video-editor",
      "/features/clip-cutter",
      "/use-cases/podcast-clipping",
      "/compare/crayo-vs-submagic",
      "/compare/crayo-vs-captions-ai",
    ],
    title: "AI Captions",
    answer:
      "AI Captions mentranskripsi suara di dalam klip dan mengubahnya jadi subtitle animasi yang sinkron per kata, dengan 32 template caption dan transkripsi untuk 56 bahasa lisan. Cocok untuk clipper, podcaster, dan streamer yang ingin setiap video pendeknya punya caption agar tetap enak ditonton tanpa suara, tanpa perlu mengetik sepatah kata pun.",
    description:
      "Caption akurat dan beranimasi di setiap klip. Pilih template, sorot kata kunci, dan atur gayanya di editor.",
    heroPrefix: "Tambahkan",
    heroHighlight: "caption AI",
    heroSuffix: "ke setiap klip secara otomatis",
    heroImage: "/features/ai-captions/ai-captions-hero.png",
    buttonText: "Buat Caption Sekarang",
    buttonLink: "/create/auto-clip",
    seoTitle: "Generator Caption AI untuk Shorts, Reels, dan TikTok",
    seoDescription:
      "Buat caption animasi untuk Shorts, Reels, dan TikTok dengan AI. 32 template caption, 56 bahasa lisan, sorotan kata kunci, dan kontrol gaya penuh.",
    faqs: [
      {
        question: "Di mana caption ditambahkan?",
        answer:
          "Auto Clip dan AI Video Editor memberi caption pada setiap klip yang dihasilkan. Di editor timeline kamu juga bisa menjalankan subtitle AI pada video apa pun, atau menulis subtitle manual baris per baris.",
      },
      {
        question: "Bahasa apa saja yang bisa ditranskripsi Crayo?",
        answer:
          "56 bahasa lisan, termasuk Inggris, Spanyol, Portugis, Prancis, Jerman, Hindi, Arab, Jepang, dan Korea. Kamu memilih bahasa lisan sebelum proses dimulai supaya transkripnya memakai model yang tepat.",
      },
      {
        question: "Bisa ubah tampilan caption?",
        answer:
          "Bisa. Pilih salah satu dari 32 template, lalu atur jumlah kata per baris, posisi, warna teks, outline, bayangan, animasi, dan penekanan. Keyword pop menyorot kata-kata penting, dan perubahan bisa diterapkan ke satu baris atau semua baris.",
      },
      {
        question: "Bisa perbaiki kata yang salah ditranskripsi?",
        answer:
          "Bisa. Buka klip di editor, pilih baris caption, lalu edit teksnya. Timing dan gayanya tetap terjaga.",
      },
    ],
  },
  "find-viral-moments": {
    related: [
      "/features/auto-clip",
      "/features/clip-cutter",
      "/features/ai-captions",
      "/use-cases/clippers",
      "/calculators/clips-from-one-video",
      "/compare/crayo-vs-opus-clip",
    ],
    title: "Find Viral Moments",
    answer:
      "Find Viral Moments memindai podcast, stream, atau wawancara berdurasi hingga 3 jam, memberi skor pada momen yang paling berpotensi viral, dan menyimpan masing-masing sebagai klip tersendiri. Cocok untuk clipper dan tim podcast yang mau daftar pendeknya dulu, baru editing belakangan.",
    description:
      "Upload video panjang, beri tahu kami isinya, dan dapatkan kumpulan momen berperingkat yang bisa kamu tinjau, potong, dan simpan.",
    heroPrefix: "Temukan",
    heroHighlight: "momen viral",
    heroSuffix: "di video panjang mana pun",
    heroImage: "/features/find-viral-moments/find-viral-moments-hero.png",
    buttonText: "Cari Momen Sekarang",
    buttonLink: "/create/find-moments",
    seoTitle: "Temukan Momen Viral di Video Panjang dengan AI",
    seoDescription:
      "Upload podcast, stream, atau wawancara, lalu AI mencari dan menilai momen yang layak diposting. Tinjau, potong, dan simpan hingga 50 klip dari satu video.",
    faqs: [
      {
        question: "Apa saja yang bisa saya atur sebelum pencarian dimulai?",
        answer:
          "Berapa banyak momen yang dicari (5 sampai 50), durasi tiap momen (30, 45, 60, atau 90 detik), bahasa lisan, jenis kontennya (obrolan, podcast, wawancara, aksi, atau deteksi otomatis), dan catatan fokus opsional, misalnya hanya bagian yang membahas harga.",
      },
      {
        question: "Bisa cek momennya dulu sebelum disimpan?",
        answer:
          "Bisa. Aktifkan Tinjau sebelum menyimpan dan momen-momennya akan terbuka di Clip Cutter, tempat kamu menyimpan, melewati, atau memotong masing-masing sebelum jadi klip. Kalau dimatikan, setiap momen langsung tersimpan ke Assets begitu pencarian selesai.",
      },
      {
        question: "Seberapa panjang video sumbernya boleh?",
        answer:
          "Mulai dari 1 menit sampai 3 jam. Sumber yang lebih panjang hanya butuh waktu pindai lebih lama.",
      },
      {
        question: "Berapa biayanya dalam kredit?",
        answer:
          "Satu kredit workflow per momen yang ditemukan. Momen yang tidak kami temukan dikembalikan kreditnya, dan kamu bisa menjalankan hingga tiga pencarian sekaligus.",
      },
    ],
  },
  "ai-reframe": {
    related: [
      "/features/auto-clip",
      "/features/video-crop",
      "/features/video-editor",
      "/use-cases/podcast-clipping",
      "/compare/crayo-vs-opus-clip",
      "/compare/crayo-vs-vizard",
    ],
    title: "AI Reframe",
    answer:
      "AI Reframe mengubah rekaman 16:9 jadi klip 9:16 dengan mendeteksi wajah, mengikuti siapa pun yang sedang bicara, dan menggeser crop dengan gerakan kamera yang halus dan stabil, bukan tracking yang goyang. Cocok untuk clipper, podcaster, dan streamer yang butuh klip vertikal dengan pembicara yang selalu ada di dalam frame.",
    description:
      "Deteksi wajah dan pelacakan pembicara menjaga orang yang bicara tetap di tengah saat crop berpindah dari layar lebar ke vertikal.",
    heroPrefix: "Reframe",
    heroHighlight: "16:9 ke 9:16",
    heroSuffix: "dengan pelacakan wajah",
    heroImage: "/features/ai-reframe/ai-reframe-hero.png",
    buttonText: "Reframe Video Sekarang",
    buttonLink: "/create/auto-clip",
    seoTitle: "AI Reframe: 16:9 ke 9:16 dengan Pelacakan Wajah",
    seoDescription:
      "Ubah podcast dan stream layar lebar jadi klip vertikal. AI menemukan wajah, mengikuti pembicara aktif, dan menjaga crop tetap stabil saat berpindah.",
    faqs: [
      {
        question: "Bagaimana cara menentukan area crop?",
        answer:
          "Model deteksi wajah menemukan setiap wajah di dalam shot dan gerakan bibir memberi tahu siapa yang bicara. Crop berpusat pada pembicara aktif dan hanya bergeser ke posisi baru saat subjek keluar dari zona mati, jadi frame tidak goyang setiap kali kepala bergerak.",
      },
      {
        question: "Bagaimana dengan rekaman layar atau video tanpa wajah?",
        answer:
          "Planner mengklasifikasikan setiap klip. Rekaman layar, klip tanpa wajah yang bisa diikuti, dan klip yang satu-satunya wajahnya hanya overlay webcam kecil di pojok akan mempertahankan frame penuh, bukan crop yang buram.",
      },
      {
        question: "Layout apa saja yang bisa dipilih?",
        answer:
          "Fullscreen menempatkan pembicara memenuhi frame vertikal, Streamer menambahkan latar blur, Split menumpuk video kedua di bawah, dan Letterbox mempertahankan frame penuh tanpa crop. Output bisa 9:16, 1:1, atau 4:5.",
      },
      {
        question: "Bisa atur framing secara manual?",
        answer:
          "Bisa. Buka klip di editor, masuk ke Effects lalu Reframe, dan edit keyframe kamera, atau kunci kamera pada satu pembicara sepanjang klip.",
      },
    ],
  },
  "video-editor": {
    related: [
      "/features/auto-clip",
      "/features/ai-captions",
      "/features/ai-reframe",
      "/features/clip-cutter",
      "/compare/crayo-vs-capcut",
      "/compare/crayo-vs-veed",
    ],
    title: "Video Editor",
    answer:
      "Crayo Video Editor adalah editor timeline berbasis browser untuk klip pendek dengan track video, caption, teks, dan audio, plus zoom, transisi, LUT, speed ramp, dan keyframe kamera. Tersedia di semua paket dan dibuat untuk clipper yang merapikan hasil Auto Clip atau mengedit video upload dari nol.",
    description:
      "Timeline multi-track dengan caption, zoom, transisi, LUT, speed ramp, cutaway, dan keyframe kamera. Tanpa install, tanpa watermark di paket mana pun.",
    heroPrefix: "",
    heroHighlight: "Editor video AI",
    heroSuffix: "yang dibuat untuk klip pendek",
    heroImage: "/features/video-editor/video-editor-hero.png",
    buttonText: "Buka Editor",
    buttonLink: "/projects",
    seoTitle: "Editor Video AI untuk Klip Pendek",
    seoDescription:
      "Edit klip pendek di browser dengan timeline multi-track. Caption AI, zoom, transisi, LUT, speed ramp, dan keyframe kamera di semua paket.",
    faqs: [
      {
        question: "Apa saja yang ada di timeline?",
        answer:
          "Track terpisah untuk video, subtitle, teks, audio, dan efek. Split klip di playhead, potong bagian awal atau akhir, susun ulang, dan tumpuk cutaway di atas klip utama sementara audio di bawahnya otomatis mengecil.",
      },
      {
        question: "Efek apa saja yang tersedia?",
        answer:
          "Zoom dengan timing dan easing, transisi seperti fade, slide, whip, punch, glitch, dan flash, color grading LUT, speed ramp dengan keyframe, dan panel Camera and reframe untuk menggeser crop seiring waktu.",
      },
      {
        question: "Bagaimana caption ditangani?",
        answer:
          "Jalankan subtitle AI pada klip atau tulis manual, lalu pilih dari 32 template dan atur kata per baris, posisi, animasi, penekanan, dan keyword pop. Perubahan bisa diterapkan ke satu baris atau semua baris.",
      },
      {
        question: "Apakah editornya ada di semua paket?",
        answer:
          "Ya. Semua paket Crayo menyertakan editor lengkap. Perbedaan paket ada di jumlah menit video jadi yang bisa kamu export tiap bulan, dan pratinjau efek membutuhkan Chrome atau Edge.",
      },
    ],
  },
};
